import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  MenuItem,
  Popover,
  Stack,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Alert,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Filler,
  Tooltip as ChartTooltip,
  Legend,
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import AddIcon from '@mui/icons-material/Add';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SettingsIcon from '@mui/icons-material/Settings';
import EditNoteIcon from '@mui/icons-material/EditNote';
import TuneIcon from '@mui/icons-material/Tune';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useBrand } from '../contexts/BrandContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { auditsApi, type AuditProgress } from '../services/audits';
import { formatDate, formatDateTime } from '../i18n/format';
import { bareHost, sourceHref, isGroundingWrapper } from '../utils/sourceUrl';
import { brandQueriesApi } from '../services/brand-queries';
import { proposedArticlesApi } from '../services/proposed-articles';
import QueryManager from '../components/brand/QueryManager';
import { AiOverviewLogo, MistralLogo } from '../components/brand/EngineLogo';
import AxisScheduleControl from '../components/brand/AxisScheduleControl';
import CostEstimate from '../components/billing/CostEstimate';
import { RecommendationList } from '../components/recommendations/RecommendationList';
import { useTranslation, Trans } from 'react-i18next';
import { StrategySuggestDialog } from '../components/recommendations/StrategySuggestDialog';
import { api } from '../services/api';
import { fromBrandAxis, fromStringList, BRAND_STRATEGY_LIMITS, isBrandOwnedHost } from '@traaker/shared';
import type {
  Audit,
  AuditType,
  UnifiedRecommendation,
  BrandAxisAuditType,
  AxisMatrixAuditPoint,
  BrandAxisRecommendation,
  BrandNarrativeSummary,
  BrandAuthoritySummary,
  BrandReferralSummary,
  BrandQuery,
  BrandQueryAxis,
  SchedulableAuditType,
} from '@traaker/shared';
import { CATEGORY_CHIP_COLOR, sourceColorsFrom } from '../utils/targetColors';
// The answer renderer lives in components/common so the Conversations transcript
// renders an engine answer exactly the way this page does — one copy.
import {
  extractDomain,
  HighlightedResponse,
  useMarqueColors,
} from '../components/common/HighlightedResponse';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Filler, ChartTooltip, Legend);

type AxisAgent = 'brand_narrative' | 'brand_authority' | 'brand_referral';

const AXIS_META: Record<BrandQueryAxis, {
  /** i18n keys — resolved with `t()` at render. */
  titleKey: string;
  subtitleKey: string;
  auditType: AuditType;
  agentType: AxisAgent;
  color: string;
}> = {
  narrative: {
    titleKey: 'axisPage.narrative.title',
    subtitleKey: 'axisPage.narrative.subtitle',
    auditType: 'brand_narrative',
    agentType: 'brand_narrative',
    color: '#8B5CF6',
  },
  authority: {
    titleKey: 'axisPage.authority.title',
    subtitleKey: 'axisPage.authority.subtitle',
    auditType: 'brand_authority',
    agentType: 'brand_authority',
    color: '#00DDB3',
  },
  referral: {
    titleKey: 'axisPage.referral.title',
    subtitleKey: 'axisPage.referral.subtitle',
    auditType: 'brand_referral',
    agentType: 'brand_referral',
    color: '#FF6B35',
  },
};


const ALIGNMENT_COLORS: Record<string, string> = {
  strong: '#00DDB3',
  partial: '#FF6B35',
  weak: '#FF4444',
  misaligned: '#FF4444',
};

/** i18n keys — resolved with `t()` at render. */
const ALIGNMENT_LABEL_KEYS: Record<string, string> = {
  strong: 'axisPage.alignment.strong',
  partial: 'axisPage.alignment.partial',
  weak: 'axisPage.alignment.weak',
  misaligned: 'axisPage.alignment.misaligned',
};

function isBrandUrl(url: string, brandName?: string, brandUrl?: string) {
  if (!brandName && !brandUrl) return false;
  const ref = bareHost(brandUrl || brandName);
  if (!ref) return false;
  // Compare bare hostnames so `vitalis-poitiers.fr` source matches a brand
  // record stored as `https://www.vitalis-poitiers.fr/`. Ownership (apex,
  // subdomain, sibling TLD) is `isBrandOwnedHost` — the same rule the BP agent's
  // `scoring.is_brand_host` uses to compute the persisted `brandIsSource`, so the
  // green chip can never contradict the "Votre site est source" badge next to it.
  const host = bareHost(url);
  if (host) return isBrandOwnedHost(host, ref);
  // Fallback to string-include for free-text brand names that don't parse
  // as URLs (e.g. brandUrl is empty and we got the brand name as ref).
  return url.toLowerCase().includes(ref);
}

type EngineKey =
  | 'perplexity'
  | 'openai'
  | 'gemini'
  | 'claude'
  | 'mistral'
  | 'ai_overview'
  | 'unknown';

// `secondary: true` = a simulated engine (a model + a web-search harness we own),
// shown with an "indicatif" chip. It approximates a product, it isn't the product.
const ENGINE_META: Record<EngineKey, { label: string; color: string; bg: string; secondary?: boolean }> = {
  perplexity: { label: 'Perplexity', color: '#20B8CD', bg: '#20B8CD22' },
  openai: { label: 'ChatGPT', color: '#10A37F', bg: '#10A37F22' },
  gemini: { label: 'Gemini', color: '#4285F4', bg: '#4285F422' },
  claude: { label: 'Claude', color: '#D97757', bg: '#D9775722' },
  mistral: { label: 'Mistral', color: '#FF7000', bg: '#FF700022', secondary: true },
  // Google red, not Google blue — Gemini already owns #4285F4 and the two would
  // be indistinguishable as chart series.
  ai_overview: { label: 'AI Overview', color: '#EA4335', bg: '#EA433522', secondary: true },
  unknown: { label: 'Moteur', color: '#8B9DC3', bg: '#8B9DC322' },
};

const KNOWN_ENGINE_KEYS = new Set<EngineKey>([
  'perplexity',
  'openai',
  'gemini',
  'claude',
  'mistral',
  'ai_overview',
]);

function engineKey(e?: string): EngineKey {
  const k = (e || '').toLowerCase() as EngineKey;
  return KNOWN_ENGINE_KEYS.has(k) ? k : 'unknown';
}

function EngineLogo({ engine, size = 20 }: { engine?: string; size?: number }) {
  const k = engineKey(engine);
  const meta = ENGINE_META[k];
  const common = {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    style: { display: 'block' as const },
  };
  if (k === 'perplexity') {
    return (
      <svg {...common} fill={meta.color} aria-label="Perplexity">
        <path d="M12 2.5 3.5 7.5v9L12 21.5l8.5-5v-9L12 2.5Zm0 2.31 6.5 3.82v7.74L12 19.19l-6.5-3.82V8.63L12 4.81Z" />
        <path d="M7.5 9.5h9v1.5h-9zM7.5 12h9v1.5h-9z" />
      </svg>
    );
  }
  if (k === 'openai') {
    return (
      <svg {...common} fill={meta.color} aria-label="ChatGPT">
        <path d="M22.28 9.82a5.98 5.98 0 0 0-.52-4.91 6.05 6.05 0 0 0-6.52-2.9A6 6 0 0 0 5.07 4.16a5.98 5.98 0 0 0-4 2.91 6.05 6.05 0 0 0 .75 7.11 5.98 5.98 0 0 0 .52 4.91 6.05 6.05 0 0 0 6.52 2.9A6 6 0 0 0 18.93 19.84a5.98 5.98 0 0 0 4-2.91 6.05 6.05 0 0 0-.65-7.11ZM13.19 21.1a4.49 4.49 0 0 1-2.88-1.04l.14-.08 4.77-2.76a.78.78 0 0 0 .39-.68v-6.75L17.63 11v5.57a4.5 4.5 0 0 1-4.44 4.53Zm-9.66-4.12a4.48 4.48 0 0 1-.54-3.03l.14.08 4.78 2.76a.77.77 0 0 0 .78 0l5.84-3.37v2.33L10.7 18.5a4.5 4.5 0 0 1-6.17-1.52ZM2.56 8.66a4.49 4.49 0 0 1 2.34-1.97v5.68a.77.77 0 0 0 .39.67l5.81 3.35-2.02 1.16a.07.07 0 0 1-.07 0l-4.83-2.79a4.5 4.5 0 0 1-1.62-6.1Zm16.57 3.86-5.83-3.4 2.02-1.16a.07.07 0 0 1 .07 0l4.82 2.79a4.5 4.5 0 0 1-.67 8.1v-5.68a.78.78 0 0 0-.41-.65Zm2.02-3.03-.14-.09L16.23 6.6a.77.77 0 0 0-.79 0L9.62 9.97V7.64l4.83-2.79a4.5 4.5 0 0 1 6.7 4.64ZM8.52 13.57 6.5 12.4V6.84a4.5 4.5 0 0 1 7.38-3.46l-.14.08L8.97 6.22a.78.78 0 0 0-.39.67Zm1.1-2.37L12.22 9.67l2.62 1.5v3.01l-2.6 1.51-2.62-1.5Z" />
      </svg>
    );
  }
  if (k === 'gemini') {
    return (
      <svg {...common} viewBox="0 0 24 24" aria-label="Gemini">
        <defs>
          <linearGradient id="gm" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#4285F4" />
            <stop offset="50%" stopColor="#9B72F2" />
            <stop offset="100%" stopColor="#D96570" />
          </linearGradient>
        </defs>
        <path
          fill="url(#gm)"
          d="M12 2c.4 4.6 2.4 6.6 7 7-4.6.4-6.6 2.4-7 7-.4-4.6-2.4-6.6-7-7 4.6-.4 6.6-2.4 7-7Z"
        />
      </svg>
    );
  }
  if (k === 'claude') {
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 100 100"
        fill={meta.color}
        aria-label="Claude"
        style={{ display: 'block' }}
      >
        <path d="m19.6 66.5 19.7-11 .3-1-.3-.5h-1l-3.3-.2-11.2-.3L14 53l-9.5-.5-2.4-.5L0 49l.2-1.5 2-1.3 2.9.2 6.3.5 9.5.6 6.9.4L38 49.1h1.6l.2-.7-.5-.4-.4-.4L29 41l-10.6-7-5.6-4.1-3-2-1.5-2-.6-4.2 2.7-3 3.7.3.9.2 3.7 2.9 8 6.1L37 36l1.5 1.2.6-.4.1-.3-.7-1.1L33 25l-6-10.4-2.7-4.3-.7-2.6c-.3-1-.4-2-.4-3l3-4.2L28 0l4.2.6L33.8 2l2.6 6 4.1 9.3L47 29.9l2 3.8 1 3.4.3 1h.7v-.5l.5-7.2 1-8.7 1-11.2.3-3.2 1.6-3.8 3-2L61 2.6l2 2.9-.3 1.8-1.1 7.7L59 27.1l-1.5 8.2h.9l1-1.1 4.1-5.4 6.9-8.6 3-3.5L77 13l2.3-1.8h4.3l3.1 4.7-1.4 4.9-4.4 5.6-3.7 4.7-5.3 7.1-3.2 5.7.3.4h.7l12-2.6 6.4-1.1 7.6-1.3 3.5 1.6.4 1.6-1.4 3.4-8.2 2-9.6 2-14.3 3.3-.2.1.2.3 6.4.6 2.8.2h6.8l12.6 1 3.3 2 1.9 2.7-.3 2-5.1 2.6-6.8-1.6-16-3.8-5.4-1.3h-.8v.4l4.6 4.5 8.3 7.5L89 80.1l.5 2.4-1.3 2-1.4-.2-9.2-7-3.6-3-8-6.8h-.5v.7l1.8 2.7 9.8 14.7.5 4.5-.7 1.4-2.6 1-2.7-.6-5.8-8-6-9-4.7-8.2-.5.4-2.9 30.2-1.3 1.5-3 1.2-2.5-2-1.4-3 1.4-6.2 1.6-8 1.3-6.4 1.2-7.9.7-2.6v-.2H49L43 72l-9 12.3-7.2 7.6-1.7.7-3-1.5.3-2.8L24 86l10-12.8 6-7.9 4-4.6-.1-.5h-.3L17.2 77.4l-4.7.6-2-2 .2-3 1-1 8-5.5Z" />
      </svg>
    );
  }
  if (k === 'mistral') {
    return <MistralLogo size={size} />;
  }
  if (k === 'ai_overview') {
    return <AiOverviewLogo size={size} />;
  }
  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: '50%',
        bgcolor: meta.bg,
        color: meta.color,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: size * 0.55,
        fontWeight: 700,
      }}
    >
      ?
    </Box>
  );
}

export default function BrandAxisPage({ axis }: { axis: BrandQueryAxis }) {
  const meta = AXIS_META[axis];
  const { t } = useTranslation();
  const metaTitle = t(meta.titleKey);
  const { activeBrand, refreshBrands } = useBrand();
  const { tenant } = useAuth();
  // Manual run-now is agency-only (others cadence-driven; API enforces, allowing
  // only the onboarding first-run).
  const isAgency = tenant?.tier === 'agency';
  const [queries, setQueries] = useState<BrandQuery[]>([]);
  const [matrix, setMatrix] = useState<AxisMatrixAuditPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [configOpen, setConfigOpen] = useState(false);
  const [scoreInfoAnchor, setScoreInfoAnchor] = useState<HTMLElement | null>(null);
  const [latestAudit, setLatestAudit] = useState<Audit | null>(null);
  // Completed audits (oldest→newest) with embedded summaries — drives the
  // time-series charts in the header (score/alignment trend + sentiment), which
  // need fields like sentiment_breakdown that the matrix points don't carry.
  const [completedAudits, setCompletedAudits] = useState<Audit[]>([]);

  // The real enforced cap is the tenant-wide `prompts` gauge (all brands +
  // axes), surfaced in the popup. The button just shows this axis's active
  // count for quick context — mixing a per-axis count with a tenant-wide cap
  // would be misleading.

  // `queries` holds active prompts only (drives KPI counts + create gating);
  // `archivedQueries` holds archived rows so the matrix can offer re-activate.
  const [archivedQueries, setArchivedQueries] = useState<BrandQuery[]>([]);
  // The most recent audit of any status (running/pending/completed/failed) —
  // separate from `latestAudit` (which is the latest *completed* audit and
  // drives the matrix expansion fetch). Drives the "audit en cours" banner.
  const [inFlightAudit, setInFlightAudit] = useState<Audit | null>(null);
  // Live fan-out completion progress for the in-flight audit (3 split axes only).
  const [progress, setProgress] = useState<AuditProgress | null>(null);

  // `background: true` skips the full-page loading spinner — used by the in-flight
  // progress poll so a mid-audit refresh never blanks the page to a spinner. Only
  // the initial mount (and brand switch) shows the full-page CircularProgress.
  const reload = useCallback(async (opts?: { background?: boolean }) => {
    const background = opts?.background === true;
    if (!activeBrand?.brandId) {
      setQueries([]);
      setArchivedQueries([]);
      setMatrix([]);
      setLatestAudit(null);
      setCompletedAudits([]);
      setInFlightAudit(null);
      setLoading(false);
      return;
    }
    if (!background) setLoading(true);
    try {
      const [allQueries, m, allAudits] = await Promise.all([
        brandQueriesApi.list(activeBrand.brandId, axis),
        auditsApi.getAxisMatrix(activeBrand.brandId, meta.auditType as any, 12),
        // The matrix only carries completed audits; pull the unfiltered list
        // so a freshly enqueued / running audit shows up as a banner.
        auditsApi.list(meta.auditType, activeBrand.brandId).catch(() => [] as Audit[]),
      ]);
      setQueries(allQueries.filter((q) => q.status === 'active'));
      setArchivedQueries(allQueries.filter((q) => q.status === 'archived'));
      setMatrix(m.audits);

      // Completed audits oldest→newest for the trend charts (the list arrives
      // newest-first from the type-date GSI).
      setCompletedAudits(
        allAudits
          .filter((a) => a.status === 'completed')
          .sort((a, b) => a.createdAt.localeCompare(b.createdAt)),
      );

      // Most recent audit by createdAt regardless of status — the API list
      // returns newest-first via the type-date GSI, so head of array wins.
      const mostRecent = allAudits[0] || null;
      setInFlightAudit(
        mostRecent && (mostRecent.status === 'pending' || mostRecent.status === 'running')
          ? mostRecent
          : null,
      );

      // Hydrate the latest *completed* Audit row (drives the matrix expansion fetch).
      if (m.audits.length > 0) {
        try {
          const a = await auditsApi.getById(m.audits[0].auditId);
          setLatestAudit(a || null);
        } catch {
          setLatestAudit(null);
        }
      } else {
        setLatestAudit(null);
      }
      setError('');
    } catch (err: any) {
      setError(err?.message || t('axisPage.error.load'));
    } finally {
      setLoading(false);
    }
  }, [activeBrand?.brandId, axis, meta.auditType, t]);

  // Single in-flight loop: poll /progress every 4s for the live percentage AND
  // trigger ONE matrix reload the moment progress reports `done` — so the
  // results refresh + banner clears within ~4s of completion (no separate blind
  // 20s timer needed). Visibility-aware. Best-effort: a failed poll keeps the
  // last bar value.
  useEffect(() => {
    if (!inFlightAudit) {
      setProgress(null);
      return;
    }
    let cancelled = false;
    const poll = async () => {
      if (document.visibilityState !== 'visible') return;
      try {
        const p = await auditsApi.getProgress(inFlightAudit.auditId, meta.auditType as BrandAxisAuditType);
        if (cancelled) return;
        setProgress(p);
        // On `done`, reload to refresh matrix/summary + re-derive inFlightAudit.
        // We DON'T one-shot-guard this: AGENT_DONE (→ progress=done) lands a beat
        // before the phase-checker flips the audit row to `completed`, so the
        // first reload may still see `running`. Reloading each `done` poll
        // self-terminates once the row flips → inFlightAudit clears → this effect
        // tears down and polling stops (typically 1 extra reload, ~4s).
        if (p.state === 'done') {
          reload({ background: true }); // refresh results without blanking the page
        }
      } catch {
        /* leave the last value */
      }
    };
    poll();
    const id = window.setInterval(poll, 4_000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [inFlightAudit, meta.auditType, reload]);

  useEffect(() => {
    reload();
  }, [reload]);

  const handleCreate = async () => {
    setCreating(true);
    setError('');
    try {
      await auditsApi.create(meta.auditType, {}, activeBrand?.brandId);
      setCreateDialogOpen(false);
      await reload();
    } catch (err: any) {
      setError(err?.message || t('axisPage.error.create'));
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const latest = matrix[0] || null;
  const summary: BrandNarrativeSummary | BrandAuthoritySummary | BrandReferralSummary | undefined =
    axis === 'narrative'
      ? latestAudit?.brandNarrativeSummary
      : axis === 'authority'
        ? latestAudit?.brandAuthoritySummary
        : latestAudit?.brandReferralSummary;
  const recosV2: BrandAxisRecommendation[] = (summary as any)?.recommendations_v2 || [];
  const recosLegacy: string[] = summary?.recommendations || [];
  const auditTypeForAxis: AuditType =
    axis === 'narrative' ? 'brand_narrative' : axis === 'authority' ? 'brand_authority' : 'brand_referral';
  const unifiedRecos: UnifiedRecommendation[] = recosV2.length
    ? fromBrandAxis(recosV2, { auditType: auditTypeForAxis, originLabel: metaTitle })
    : fromStringList(recosLegacy, { auditType: auditTypeForAxis, originLabel: metaTitle });

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ width: 12, height: 12, borderRadius: 0.6, bgcolor: meta.color }} />
          <Typography variant="h4" fontWeight={700} data-tour="page-intro">
            {metaTitle}
          </Typography>
          <IconButton
            size="small"
            aria-label={t('axisPage.scoreInfo.aria')}
            onClick={(e) => setScoreInfoAnchor(e.currentTarget)}
            sx={{ color: 'text.secondary' }}
          >
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
          <ScoreInfoPopover
            axis={axis}
            color={meta.color}
            anchorEl={scoreInfoAnchor}
            onClose={() => setScoreInfoAnchor(null)}
          />
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            data-tour="prompt-config"
            variant="outlined"
            startIcon={<SettingsIcon />}
            onClick={() => setConfigOpen(true)}
            disabled={!activeBrand?.brandId}
          >
            {t('axisPage.configurePrompts', { count: queries.length })}
          </Button>
          {activeBrand?.brandId && (
            <Box component="span" data-tour="schedule-control" sx={{ display: 'inline-flex' }}>
              <AxisScheduleControl
                brandId={activeBrand.brandId}
                tier={tenant?.tier || ''}
                axis={meta.auditType as SchedulableAuditType}
                cadenceOverride={activeBrand.auditConfig?.cadenceOverride}
                onSaved={refreshBrands}
              />
            </Box>
          )}
          {isAgency && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateDialogOpen(true)}
              disabled={!activeBrand?.brandId || queries.length === 0}
            >
              {t('axisPage.newAudit')}
            </Button>
          )}
        </Stack>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t(meta.subtitleKey)}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Empty-state gate: must configure at least one prompt before audits are useful. */}
      {queries.length === 0 ? (
        <Card>
          <CardContent sx={{ p: 5, textAlign: 'center' }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              {t('axisPage.empty.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 520, mx: 'auto' }}>
              {t('axisPage.empty.description')}
            </Typography>
            <Button
              variant="contained"
              startIcon={<SettingsIcon />}
              onClick={() => setConfigOpen(true)}
              disabled={!activeBrand?.brandId}
            >
              {t('axisPage.empty.cta')}
            </Button>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
              {t('axisPage.empty.hint')}
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <>
          <AxisChartsHeader axis={axis} matrix={matrix} audits={completedAudits} color={meta.color} />

          {inFlightAudit && (
            <Card sx={{ mb: 3, borderLeft: `4px solid ${meta.color}` }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: '14px !important' }}>
                <CircularProgress size={22} sx={{ color: meta.color }} />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ color: meta.color }}>
                    {t('axisPage.inProgress.title', { axis: metaTitle.toLowerCase() })}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {progress && progress.state === 'running' && progress.expected > 0
                      ? t('axisPage.inProgress.running', {
                          completed: progress.completed,
                          expected: progress.expected,
                        })
                      : t('axisPage.inProgress.started', {
                          date: formatDateTime(inFlightAudit.createdAt, {
                            day: '2-digit',
                            month: 'short',
                            hour: '2-digit',
                            minute: '2-digit',
                          }),
                        })}
                  </Typography>
                </Box>
                <Chip
                  label={
                    progress && progress.state === 'running' && progress.expected > 0
                      ? `${progress.pct}%`
                      : inFlightAudit.status === 'pending'
                        ? t('axisPage.status.pending')
                        : t('axisPage.status.running')
                  }
                  size="small"
                  sx={{
                    bgcolor: `${meta.color}1A`,
                    color: meta.color,
                    fontWeight: 700,
                  }}
                />
              </CardContent>
              <LinearProgress
                {...(progress && progress.state === 'running' && progress.expected > 0
                  ? { variant: 'determinate' as const, value: progress.pct }
                  : {})}
                sx={{
                  height: 3,
                  bgcolor: `${meta.color}22`,
                  '& .MuiLinearProgress-bar': { bgcolor: meta.color },
                }}
              />
            </Card>
          )}

          {summary && (
            <AxisSummaryBlock axis={axis} summary={summary} color={meta.color} />
          )}

          {latest && latestAudit ? (
            <AxisMatrixTable
              axis={axis}
              queries={queries}
              archivedQueries={archivedQueries}
              matrix={matrix}
              latestAudit={latestAudit}
              latestAuditMeta={meta}
              brand={activeBrand}
              onReactivate={async (queryId) => {
                if (!activeBrand?.brandId) return;
                try {
                  await brandQueriesApi.update(activeBrand.brandId, queryId, {
                    axis,
                    status: 'active',
                  });
                  await reload();
                } catch (err: any) {
                  setError(err?.message || t('axisPage.error.reactivate'));
                }
              }}
              onArchive={async (queryId) => {
                if (!activeBrand?.brandId) return;
                try {
                  await brandQueriesApi.archive(activeBrand.brandId, axis, queryId);
                  await reload();
                } catch (err: any) {
                  setError(err?.message || t('axisPage.error.archive'));
                }
              }}
              onReactivateByText={async (text) => {
                if (!activeBrand?.brandId) return;
                try {
                  await brandQueriesApi.create(activeBrand.brandId, { axis, text });
                  await reload();
                } catch (err: any) {
                  setError(err?.message || t('axisPage.error.recreate'));
                }
              }}
            />
          ) : (
            <Card>
              <CardContent sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {isAgency
                    ? t('axisPage.noAudit.agency')
                    : t('axisPage.noAudit.selfServe')}
                </Typography>
                {isAgency && (
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setCreateDialogOpen(true)}
                    disabled={!activeBrand?.brandId}
                  >
                    {t('axisPage.noAudit.cta')}
                  </Button>
                )}
              </CardContent>
            </Card>
          )}

          {unifiedRecos.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <RecommendationList
                recommendations={unifiedRecos}
                auditId={latest?.auditId || ''}
                brandId={activeBrand?.brandId}
                subtitle={t('axisPage.recos.subtitle')}
              />
            </Box>
          )}
        </>
      )}

      {/* Configure prompts modal */}
      <Dialog open={configOpen} onClose={() => setConfigOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{t('axisPage.configDialog.title', { axis: metaTitle })}</DialogTitle>
        <DialogContent dividers>
          {activeBrand && (
            <QueryManager brand={activeBrand} axis={axis} onChanged={reload} />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfigOpen(false)}>{t('axisPage.close')}</Button>
        </DialogActions>
      </Dialog>

      {/* Create audit modal */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('axisPage.createDialog.title', { axis: metaTitle })}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Typography variant="body1">
              <Trans
                i18nKey="axisPage.createDialog.launchFor"
                values={{ axis: metaTitle.toLowerCase(), brand: activeBrand?.name || '—' }}
                components={{ strong: <strong /> }}
              />
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('axisPage.createDialog.promptsInfo', { count: queries.length })}
            </Typography>
            <CostEstimate
              auditType={`brand_${axis}` as AuditType}
              promptCount={queries.length}
              competitorCount={axis === 'referral' ? activeBrand?.competitors?.length ?? 0 : undefined}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCreateDialogOpen(false)}>{t('axisPage.cancel')}</Button>
          <Button variant="contained" onClick={handleCreate} disabled={creating}>
            {creating ? <CircularProgress size={20} /> : t('axisPage.launch')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ── Charts header above the matrix (time-series + sentiment) ──

const AXIS_CHART_DEFAULTS = {
  responsive: true,
  maintainAspectRatio: false,
  interaction: { intersect: false, mode: 'index' as const },
  plugins: {
    legend: { position: 'bottom' as const, labels: { usePointStyle: true, padding: 16, font: { size: 12 } } },
    tooltip: { backgroundColor: '#FFFFFF', titleColor: '#0B0B0F', bodyColor: '#0B0B0F', borderColor: '#E6E7EB', borderWidth: 1, cornerRadius: 8, padding: 12 },
  },
  scales: {
    y: { min: 0, max: 100, grid: { color: 'rgba(11,11,15,0.06)' }, ticks: { maxTicksLimit: 6 } },
    x: { grid: { display: false }, ticks: { maxTicksLimit: 8, autoSkip: true, maxRotation: 0 } },
  },
};

const SENTIMENT_COLORS = { positive: '#10B981', neutral: '#A0A4AE', negative: '#EF4444' };
const ALIGNMENT_WEIGHTS: Record<string, number> = { strong: 100, partial: 60, weak: 30, misaligned: 0 };

function axisChartFmt(iso: string): string {
  return formatDate(iso, { day: '2-digit', month: 'short' });
}

function AxisChartCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent sx={{ p: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>{title}</Typography>
        {children}
      </CardContent>
    </Card>
  );
}

/**
 * Customer-facing "how is this score computed?" popover, anchored to the small
 * ⓘ next to the axis title. Plain-language mirror of the deterministic per-axis
 * formula in the agent's scorer — no engine names, no internal weighting jargon.
 */
function ScoreInfoPopover({
  axis,
  color,
  anchorEl,
  onClose,
}: {
  axis: BrandQueryAxis;
  color: string;
  anchorEl: HTMLElement | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const rows = t(`axisPage.scoreInfo.${axis}.rows`, { returnObjects: true }) as string[];
  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      slotProps={{ paper: { sx: { maxWidth: 380, p: 2.5, borderRadius: 2 } } }}
    >
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5, color }}>
        {t('axisPage.scoreInfo.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {t(`axisPage.scoreInfo.${axis}.intro`)}
      </Typography>
      <Stack spacing={0.75}>
        {Array.isArray(rows) &&
          rows.map((line, i) => (
            <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: color, mt: '6px', flexShrink: 0 }} />
              <Typography variant="body2">{line}</Typography>
            </Box>
          ))}
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
        {t('axisPage.scoreInfo.footer')}
      </Typography>
    </Popover>
  );
}

/**
 * Time-series header: the former per-axis Dashboard charts, moved here when the
 * Dashboard tabs were removed. Left = score trend + the axis-specific secondary
 * metric; right = sentiment breakdown (narrative/referral) or thematic topics
 * (authority). Uses completed audits (with embedded summaries) oldest→newest.
 */
function AxisChartsHeader({
  axis,
  audits,
  color,
}: {
  axis: BrandQueryAxis;
  matrix: AxisMatrixAuditPoint[];
  audits: Audit[];
  color: string;
}) {
  const { t } = useTranslation();
  if (audits.length === 0) {
    return (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="body2" color="text.secondary">
            {t('axisPage.charts.empty')}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const labels = audits.map((a) => axisChartFmt(a.createdAt));
  const pointRadius = audits.length <= 2 ? 4 : 0;

  if (axis === 'authority') {
    const data = {
      labels,
      datasets: [
        { label: t('axisPage.charts.scoreAuthority'), data: audits.map((a) => a.brandAuthoritySummary?.score ?? null), borderColor: color, borderWidth: 3, tension: 0.3, spanGaps: true, pointRadius },
        { label: t('axisPage.charts.domainCitation'), data: audits.map((a) => (a.brandAuthoritySummary?.domain_citation_rate ?? 0) * 100), borderColor: '#10B981', tension: 0.3, spanGaps: true, pointRadius },
        { label: t('axisPage.charts.responseMention'), data: audits.map((a) => (a.brandAuthoritySummary?.response_text_mention_rate ?? 0) * 100), borderColor: '#7C3AED', borderDash: [5, 5], tension: 0.3, spanGaps: true, pointRadius },
      ],
    };
    const latest = audits[audits.length - 1]?.brandAuthoritySummary;
    const cited = latest?.cited_topics ?? [];
    const missing = latest?.missing_topics ?? [];
    const hasTopics = cited.length > 0 || missing.length > 0;
    return (
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: hasTopics ? 7 : 12 }}>
          <AxisChartCard title={t('axisPage.charts.authorityOverTime')}>
            <Box sx={{ height: 300 }}><Line data={data} options={AXIS_CHART_DEFAULTS} /></Box>
          </AxisChartCard>
        </Grid>
        {hasTopics && (
          <Grid size={{ xs: 12, md: 5 }}>
            <AxisChartCard title={t('axisPage.charts.topicsLatest')}>
              <Box sx={{ height: 300, overflow: 'auto' }}>
                {cited.length > 0 && (
                  <>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: '0.04em' }}>{t('axisPage.charts.citedAsSource')}</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5, mb: 2 }}>
                      {cited.map((t, i) => (
                        <Box key={`c${i}`} sx={{ bgcolor: `${color}20`, color, fontSize: '0.75rem', fontWeight: 600, px: 1, py: 0.5, borderRadius: 1 }}>{t}</Box>
                      ))}
                    </Box>
                  </>
                )}
                {missing.length > 0 && (
                  <>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: '0.04em' }}>{t('axisPage.charts.gapsToFill')}</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                      {missing.map((t, i) => (
                        <Box key={`m${i}`} sx={{ border: '1px solid #7C3AED40', color: 'text.secondary', fontSize: '0.75rem', px: 1, py: 0.5, borderRadius: 1 }}>{t}</Box>
                      ))}
                    </Box>
                  </>
                )}
              </Box>
            </AxisChartCard>
          </Grid>
        )}
      </Grid>
    );
  }

  // narrative + referral: score trend (+ secondary) on the left, sentiment on the right.
  const trendData =
    axis === 'narrative'
      ? {
          labels,
          datasets: [
            { label: t('axisPage.charts.scoreNarrative'), data: audits.map((a) => a.brandNarrativeSummary?.score ?? null), borderColor: color, borderWidth: 3, tension: 0.3, spanGaps: true, pointRadius },
            {
              label: t('axisPage.charts.alignmentWeighted'),
              data: audits.map((a) => {
                const w = a.brandNarrativeSummary ? ALIGNMENT_WEIGHTS[a.brandNarrativeSummary.alignment] : undefined;
                return typeof w === 'number' ? w : null;
              }),
              borderColor: '#7C3AED',
              borderDash: [5, 5],
              tension: 0.3,
              spanGaps: true,
              pointRadius,
            },
          ],
        }
      : {
          labels,
          datasets: [
            { label: t('axisPage.charts.scoreReferral'), data: audits.map((a) => a.brandReferralSummary?.score ?? null), borderColor: color, borderWidth: 3, tension: 0.3, spanGaps: true, pointRadius },
            { label: t('axisPage.charts.citationRate'), data: audits.map((a) => (a.brandReferralSummary?.overall_citation_rate ?? 0) * 100), borderColor: '#10B981', tension: 0.3, spanGaps: true, pointRadius },
            { label: t('axisPage.charts.recommendationRate'), data: audits.map((a) => (a.brandReferralSummary?.overall_recommendation_rate ?? 0) * 100), borderColor: '#7C3AED', borderDash: [5, 5], tension: 0.3, spanGaps: true, pointRadius },
          ],
        };

  const sentimentOf = (a: Audit) =>
    axis === 'narrative' ? a.brandNarrativeSummary?.sentiment_breakdown : a.brandReferralSummary?.sentiment_breakdown;
  const sentimentData = {
    labels,
    datasets: [
      { label: t('axisPage.sentiment.positive'), data: audits.map((a) => sentimentOf(a)?.positive ?? 0), backgroundColor: SENTIMENT_COLORS.positive, borderRadius: 4 },
      { label: t('axisPage.sentiment.neutral'), data: audits.map((a) => sentimentOf(a)?.neutral ?? 0), backgroundColor: SENTIMENT_COLORS.neutral, borderRadius: 4 },
      { label: t('axisPage.sentiment.negative'), data: audits.map((a) => sentimentOf(a)?.negative ?? 0), backgroundColor: SENTIMENT_COLORS.negative, borderRadius: 4 },
    ],
  };
  const stackedOptions = {
    ...AXIS_CHART_DEFAULTS,
    scales: {
      y: { stacked: true, min: 0, grid: { color: 'rgba(11,11,15,0.06)' } },
      x: { stacked: true, grid: { display: false } },
    },
  };

  return (
    <Grid container spacing={2} sx={{ mb: 3 }}>
      <Grid size={{ xs: 12, md: 7 }}>
        <AxisChartCard title={axis === 'narrative' ? t('axisPage.charts.narrativeTrendTitle') : t('axisPage.charts.referralTrendTitle')}>
          <Box sx={{ height: 300 }}><Line data={trendData} options={AXIS_CHART_DEFAULTS} /></Box>
        </AxisChartCard>
      </Grid>
      <Grid size={{ xs: 12, md: 5 }}>
        <AxisChartCard title={t('axisPage.charts.sentimentTitle')}>
          <Box sx={{ height: 300 }}><Bar data={sentimentData} options={stackedOptions} /></Box>
        </AxisChartCard>
      </Grid>
    </Grid>
  );
}

// ── Per-audit summary / perception block (sits between KPIs and matrix) ──

function AxisSummaryBlock({
  axis,
  summary,
  color,
}: {
  axis: BrandQueryAxis;
  summary: BrandNarrativeSummary | BrandAuthoritySummary | BrandReferralSummary;
  color: string;
}) {
  const { t } = useTranslation();
  if (axis === 'narrative') {
    const s = summary as BrandNarrativeSummary;
    if (!s.key_perception && !(s.pros?.length) && !(s.cons?.length)) return null;
    return (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="overline" color="text.secondary" fontWeight={700} sx={{ letterSpacing: 0.5 }}>
            {t('axisPage.summary.aiPerception')}
          </Typography>
          {s.key_perception && (
            <Typography variant="body1" sx={{ mt: 1, mb: 2 }}>
              {s.key_perception}
            </Typography>
          )}
          <Grid container spacing={2}>
            {s.pros?.length > 0 && (
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  {t('axisPage.summary.strengths')}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {s.pros.map((p, i) => (
                    <Chip
                      key={i}
                      label={p}
                      size="small"
                      sx={{ bgcolor: '#DCFCE7', color: '#15803D', fontWeight: 600 }}
                    />
                  ))}
                </Box>
              </Grid>
            )}
            {s.cons?.length > 0 && (
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  {t('axisPage.summary.weaknesses')}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {s.cons.map((c, i) => (
                    <Chip
                      key={i}
                      label={c}
                      size="small"
                      sx={{ bgcolor: '#FEE2E2', color: '#991B1B', fontWeight: 600 }}
                    />
                  ))}
                </Box>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>
    );
  }

  if (axis === 'authority') {
    const s = summary as BrandAuthoritySummary;
    if (!(s.cited_topics?.length) && !(s.missing_topics?.length)) return null;
    return (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="overline" color="text.secondary" fontWeight={700} sx={{ letterSpacing: 0.5 }}>
            {t('axisPage.summary.thematicAuthority')}
          </Typography>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            {s.cited_topics?.length > 0 && (
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  {t('axisPage.summary.citedTopics')}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {s.cited_topics.map((t, i) => (
                    <Chip
                      key={i}
                      label={t}
                      size="small"
                      sx={{ bgcolor: `${color}1A`, color, fontWeight: 600 }}
                    />
                  ))}
                </Box>
              </Grid>
            )}
            {s.missing_topics?.length > 0 && (
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  {t('axisPage.summary.missingTopics')}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {s.missing_topics.map((t, i) => (
                    <Chip
                      key={i}
                      label={t}
                      size="small"
                      sx={{ bgcolor: '#FEF3C7', color: '#92400E', fontWeight: 600 }}
                    />
                  ))}
                </Box>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>
    );
  }

  // referral — surface zone breakdown if present.
  const s = summary as BrandReferralSummary;
  const zones = s.zones ? Object.entries(s.zones) : [];
  if (zones.length === 0) return null;
  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="overline" color="text.secondary" fontWeight={700} sx={{ letterSpacing: 0.5 }}>
          {t('axisPage.summary.visibilityByZone')}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
          {zones.map(([zone, data]) => (
            <Chip
              key={zone}
              label={`${zone} · ${data.score}/100`}
              sx={{
                bgcolor: `${color}1A`,
                color,
                fontWeight: 700,
                fontSize: '0.78rem',
              }}
            />
          ))}
        </Box>
      </CardContent>
    </Card>
  );
}

// ── Matrix table ──

function scoreCellColor(score: number | undefined): { bg: string; fg: string } {
  if (score === undefined) return { bg: 'transparent', fg: '#D1D5DB' };
  if (score >= 70) return { bg: '#DCFCE7', fg: '#15803D' };
  if (score >= 40) return { bg: '#FEF3C7', fg: '#92400E' };
  return { bg: '#FEE2E2', fg: '#991B1B' };
}

/** Stable dismissal id for a "Créer du contenu" action on an authority prompt.
 *  Reuses the per-brand `dismissed-recommendations` store as per-prompt metadata,
 *  so once the topic is created the button never reappears (even after refresh). */
function contentActionId(queryTextHash: string) {
  return `authority-content:${queryTextHash}`;
}

/**
 * Two low-friction actions surfaced on an under-performing authority prompt,
 * shown inside the expanded panel (keeps the collapsed row compact):
 *  - "Créer du contenu" saves the prompt as a reusable Studio content topic
 *    (same content-target path as RecommendationCard — saves, does NOT generate).
 *    Persisted per prompt: once created it stays hidden across refreshes.
 *  - "Ajuster la stratégie" opens the shared two-layer Brand Knowledge + GEO
 *    diff dialog seeded with the prompt (POST /brands/:id/strategy/suggest).
 *    Repeatable, so it's always available.
 */
function AuthorityPromptActions({
  brandId,
  auditId,
  promptText,
  contentCreated,
  onContentCreated,
}: {
  brandId?: string;
  auditId?: string;
  promptText: string;
  /** Whether a content topic was already created for this prompt (persisted). */
  contentCreated: boolean;
  onContentCreated: () => void;
}) {
  const { tenant } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [geoOpen, setGeoOpen] = useState(false);

  const newsWatchEnabled = !!tenant?.settings?.featureFlags?.newsWatchEnabled;

  const addContentTopic = async () => {
    if (busy || contentCreated || !brandId) return;
    setBusy(true);
    try {
      await proposedArticlesApi.createContentTopic({
        brandId,
        topic: promptText,
        originLabel: `Autorité · requête à faible score`,
        sourceAuditId: auditId,
      });
      onContentCreated();
      toast.show('Ajouté au studio de création', {
        action: { label: 'Voir', onClick: () => navigate('/news-watch', { state: { focusReco: true } }) },
      });
    } catch {
      toast.show("L'action n'a pas pu être enregistrée.", { severity: 'error' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
      {newsWatchEnabled && brandId && !contentCreated && (
        <Button
          size="small"
          variant="outlined"
          disabled={busy}
          startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <EditNoteIcon sx={{ fontSize: 16 }} />}
          onClick={addContentTopic}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            color: '#0284C7',
            borderColor: 'rgba(2,132,199,0.4)',
            '&:hover': { borderColor: '#0284C7', bgcolor: 'rgba(2,132,199,0.06)' },
          }}
        >
          Créer du contenu
        </Button>
      )}
      {brandId && (
        <Button
          size="small"
          variant="outlined"
          startIcon={<TuneIcon sx={{ fontSize: 16 }} />}
          onClick={() => setGeoOpen(true)}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            color: '#B45309',
            borderColor: 'rgba(180,83,9,0.4)',
            '&:hover': { borderColor: '#B45309', bgcolor: 'rgba(180,83,9,0.06)' },
          }}
        >
          Ajuster la stratégie
        </Button>
      )}
      {geoOpen && brandId && (
        <StrategySuggestDialog
          open={geoOpen}
          onClose={() => setGeoOpen(false)}
          brandId={brandId}
          auditId={auditId}
          seedQuery={promptText}
          seedResponse={promptText}
          axis="authority"
          onApplied={() => toast.show('Stratégie mise à jour')}
        />
      )}
    </Box>
  );
}

/** Prompt scores at/above this are "good" (green band); below is low. */
const LOW_SCORE_THRESHOLD = 70;

function AxisMatrixTable({
  axis,
  queries,
  archivedQueries,
  matrix,
  latestAudit,
  latestAuditMeta,
  brand,
  onReactivate,
  onArchive,
  onReactivateByText,
}: {
  axis: BrandQueryAxis;
  queries: BrandQuery[];
  archivedQueries: BrandQuery[];
  matrix: AxisMatrixAuditPoint[];
  latestAudit: Audit;
  latestAuditMeta: typeof AXIS_META[BrandQueryAxis];
  brand: any;
  onReactivate: (queryId: string) => Promise<void>;
  onArchive: (queryId: string) => Promise<void>;
  onReactivateByText: (text: string) => Promise<void>;
}) {
  const { t } = useTranslation();
  // Build the row set: all unique queryTextHashes seen across the N audits,
  // joined with the current `queries` (active) and `archivedQueries` lists.
  // - Active rows: text taken from current query, archived=false.
  // - Archived rows: queryId carried so the "Réactiver" button can flip status.
  // - Orphan rows (in matrix history but not in DDB anymore): no queryId,
  //   archived=true, no re-activate button.
  const rows = useMemo(() => {
    const byHash = new Map<
      string,
      {
        queryTextHash: string;
        text: string;
        queryId?: string;
        archived: boolean;
        canReactivate: boolean;
        // Pulled from the BrandQuery row when available — drives the level
        // chip even when no audit has run yet.
        level?: string;
        zone?: string;
        language?: string;
        topic?: string;
      }
    >();

    for (const q of queries) {
      if (!q.textHash) continue;
      byHash.set(q.textHash, {
        queryTextHash: q.textHash,
        text: q.text,
        queryId: q.queryId,
        archived: false,
        canReactivate: false,
        level: q.level && q.level !== 'pending' ? q.level : undefined,
        zone: q.zone,
        language: q.language,
        topic: q.topic,
      });
    }
    // Archived prompts only appear in the matrix when they have at least one
    // historical audit data point in the visible window. Reasons:
    //   - never-audited archived prompt = no signal to display, just visual noise.
    //   - audited but the data falls outside the matrix's `limit` window =
    //     same effect (every column would be empty); the user can still
    //     re-activate from the Configure modal where the full archived list
    //     lives.
    const hashesWithMatrixData = new Set<string>();
    for (const audit of matrix) {
      for (const qs of audit.queryScores || []) {
        if (qs.queryTextHash) hashesWithMatrixData.add(qs.queryTextHash);
      }
    }
    for (const q of archivedQueries) {
      if (!q.textHash || byHash.has(q.textHash)) continue;
      if (!hashesWithMatrixData.has(q.textHash)) continue;
      byHash.set(q.textHash, {
        queryTextHash: q.textHash,
        text: q.text,
        queryId: q.queryId,
        archived: true,
        canReactivate: true,
        level: q.level && q.level !== 'pending' ? q.level : undefined,
        zone: q.zone,
        language: q.language,
        topic: q.topic,
      });
    }
    for (const audit of matrix) {
      for (const qs of audit.queryScores || []) {
        if (!qs.queryTextHash) continue;
        if (!byHash.has(qs.queryTextHash)) {
          // Orphan: BrandQuery row was hard-deleted, but the audit history
          // still carries the text + hash. Re-activating recreates a query
          // row with the same text — same `textHash` → stitches back to the
          // existing audit history naturally on the next audit.
          const text = qs.queryText || '';
          byHash.set(qs.queryTextHash, {
            queryTextHash: qs.queryTextHash,
            text: text || t('axisPage.matrix.promptNoText'),
            archived: true,
            // Recreate-by-text only when we still have the original prompt;
            // a placeholder string can't be reposted as a query.
            canReactivate: !!text,
          });
        }
      }
    }
    // Group prompts by language → topic (authority theme / use case) → zone,
    // then break ties by latest score desc. Mirrors the onboarding review
    // grouping. Empty facets sort last (so untagged / legacy rows trail their
    // tagged peers) via the localeCompare on a high-sentinel string. Archived
    // rows always sink to the bottom regardless of facets.
    const facet = (v?: string) => (v && v.trim() ? v.trim().toLowerCase() : '￿');
    return Array.from(byHash.values()).sort((a, b) => {
      if (a.archived !== b.archived) return a.archived ? 1 : -1;
      const byLang = facet(a.language).localeCompare(facet(b.language));
      if (byLang !== 0) return byLang;
      const byTopic = facet(a.topic).localeCompare(facet(b.topic));
      if (byTopic !== 0) return byTopic;
      const byZone = facet(a.zone).localeCompare(facet(b.zone));
      if (byZone !== 0) return byZone;
      const sa = matrix[0]?.queryScores?.find((q) => q.queryTextHash === a.queryTextHash)?.score ?? -1;
      const sb = matrix[0]?.queryScores?.find((q) => q.queryTextHash === b.queryTextHash)?.score ?? -1;
      return sb - sa;
    });
  }, [queries, archivedQueries, matrix, t]);

  const [expanded, setExpanded] = useState<string | null>(null);
  const [reactivating, setReactivating] = useState<string | null>(null);
  // Result rows + which audit they came from, keyed by queryTextHash so the
  // panel can label "from audit dated …" when it's not the latest.
  const [resultsByHash, setResultsByHash] = useState<
    Record<string, { rows: any[]; auditId: string; completedAt: string }>
  >({});
  const [loadingHash, setLoadingHash] = useState<string | null>(null);
  // Comparateur: audits sélectionnés pour comparaison (max 2), par query
  const [selectedAuditsForComparison, setSelectedAuditsForComparison] = useState<Map<string, Set<string>>>(new Map());
  // Engine sélectionné par query (synchronized across all panels for that query)
  const [selectedEngineByQuery, setSelectedEngineByQuery] = useState<Map<string, EngineKey | null>>(new Map());
  // Memoize per-audit fetches so two archived prompts pulling from the same
  // older audit only hit the API once.
  const fetchedAuditsRef = useMemo(() => new Map<string, any[]>(), []);

  // Authority-only: which prompts already have a Studio content topic created.
  // Reuses the per-brand `dismissed-recommendations` store as per-prompt
  // metadata so "Créer du contenu" stays hidden across refreshes.
  const brandId: string | undefined = brand?.brandId;
  const [contentCreated, setContentCreated] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (axis !== 'authority' || !brandId) return;
    let cancelled = false;
    auditsApi
      .listDismissedRecommendations(brandId)
      .then((ids) => { if (!cancelled) setContentCreated(new Set(ids)); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [axis, brandId]);

  const markContentCreated = useCallback(
    (queryTextHash: string) => {
      const id = contentActionId(queryTextHash);
      setContentCreated((prev) => new Set(prev).add(id));
      if (brandId) auditsApi.dismissRecommendation(brandId, id).catch(() => {});
    },
    [brandId],
  );

  // ── Facet filters (language / topic / zone) over the matrix rows ──
  // '__all__' = no filter on that facet. Options are derived from the rows
  // themselves so an axis with no zones (narrative) just doesn't show that
  // dropdown.
  const [langFilter, setLangFilter] = useState('__all__');
  const [topicFilter, setTopicFilter] = useState('__all__');
  const [zoneFilter, setZoneFilter] = useState('__all__');

  const facetOptions = useMemo(() => {
    const langs = new Set<string>();
    const topics = new Set<string>();
    const zones = new Set<string>();
    for (const r of rows) {
      if (r.language) langs.add(r.language);
      if (r.topic) topics.add(r.topic);
      if (r.zone) zones.add(r.zone);
    }
    return {
      languages: Array.from(langs).sort(),
      topics: Array.from(topics).sort(),
      zones: Array.from(zones).sort(),
    };
  }, [rows]);

  const visibleRows = useMemo(
    () =>
      rows.filter(
        (r) =>
          (langFilter === '__all__' || r.language === langFilter) &&
          (topicFilter === '__all__' || r.topic === topicFilter) &&
          (zoneFilter === '__all__' || r.zone === zoneFilter),
      ),
    [rows, langFilter, topicFilter, zoneFilter],
  );

  const handleExpand = useCallback(
    async (queryTextHash: string) => {
      if (expanded === queryTextHash) {
        setExpanded(null);
        return;
      }
      setExpanded(queryTextHash);

      // If there are already audits selected, don't reset them.
      // If not, load the latest audit as default.
      const existingSelection = selectedAuditsForComparison.get(queryTextHash);
      if (!existingSelection) {
        const matchingAudit =
          matrix.find((a) => (a.queryScores || []).some((q) => q.queryTextHash === queryTextHash))
            ?? matrix[0];
        if (matchingAudit) {
          setSelectedAuditsForComparison((prev) => {
            const next = new Map(prev);
            next.set(queryTextHash, new Set([matchingAudit.auditId]));
            return next;
          });
        }
      }

      if (resultsByHash[queryTextHash]) return;

      // Find the most recent audit (matrix is newest-first) whose query_scores
      // contains this hash. Fall back to latestAudit if nothing matches —
      // the panel will show its empty-state.
      const matchingAudit =
        matrix.find((a) => (a.queryScores || []).some((q) => q.queryTextHash === queryTextHash))
          ?? matrix[0];
      if (!matchingAudit) return;

      setLoadingHash(queryTextHash);
      try {
        let all = fetchedAuditsRef.get(matchingAudit.auditId);
        if (!all) {
          all = await auditsApi.getResults(matchingAudit.auditId, latestAuditMeta.agentType);
          fetchedAuditsRef.set(matchingAudit.auditId, all);
        }
        // Bucket every hash from this audit so peers expand instantly.
        setResultsByHash((prev) => {
          const next = { ...prev };
          const buckets: Record<string, any[]> = {};
          for (const r of all!) {
            const h = r.queryTextHash || '';
            if (!h) continue;
            (buckets[h] ||= []).push(r);
          }
          for (const [h, rows] of Object.entries(buckets)) {
            // Don't overwrite a row already loaded from a more-recent audit.
            if (!next[h]) {
              next[h] = {
                rows,
                auditId: matchingAudit.auditId,
                completedAt: matchingAudit.completedAt,
              };
            }
          }
          return next;
        });
      } finally {
        setLoadingHash(null);
      }
    },
    [expanded, resultsByHash, matrix, latestAuditMeta.agentType, fetchedAuditsRef],
  );

  // Gère la sélection/désélection d'un audit pour comparaison (max 2, per-query)
  const toggleAuditForComparison = useCallback(
    async (queryTextHash: string, auditId: string) => {
      // Déterminer si on ajoute ou retire l'audit
      const currentSelected = selectedAuditsForComparison.get(queryTextHash) || new Set();
      const isAdding = !currentSelected.has(auditId) && currentSelected.size < 2;

      // Si on ajoute, lazy-load AVANT de mettre à jour le state
      if (isAdding && !fetchedAuditsRef.has(auditId)) {
        const results = await auditsApi.getResults(auditId, latestAuditMeta.agentType);
        fetchedAuditsRef.set(auditId, results);
      }

      // PUIS mettre à jour le state
      setSelectedAuditsForComparison((prev) => {
        const next = new Map(prev);
        const selected = next.get(queryTextHash) || new Set();
        const updated = new Set(selected);

        if (updated.has(auditId)) {
          updated.delete(auditId);
        } else if (updated.size < 2) {
          updated.add(auditId);
        }

        if (updated.size === 0) {
          next.delete(queryTextHash);
          // Close the expanded panel when all audits are deselected
          setExpanded(null);
        } else {
          next.set(queryTextHash, updated);
          // Open the prompt if not already open
          setExpanded(queryTextHash);
        }
        return next;
      });
    },
    [selectedAuditsForComparison, latestAuditMeta.agentType, fetchedAuditsRef],
  );

  // Render columns oldest → latest (latest = rightmost, idiomatic timeline reading).
  // The API returns newest-first; reverse for display only.
  const orderedAudits = useMemo(() => [...matrix].reverse(), [matrix]);
  const latestAuditId = matrix[0]?.auditId;

  const hasFilters =
    facetOptions.languages.length > 1 ||
    facetOptions.topics.length > 0 ||
    facetOptions.zones.length > 1;
  const activeFilterCount =
    (langFilter !== '__all__' ? 1 : 0) +
    (topicFilter !== '__all__' ? 1 : 0) +
    (zoneFilter !== '__all__' ? 1 : 0);

  return (
    <Card sx={{ mb: 3, overflow: 'hidden' }}>
      {hasFilters && (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 1.5,
            px: 2,
            py: 1.5,
            borderBottom: '1px solid rgba(11,11,15,0.08)',
            bgcolor: '#FAFBFC',
          }}
        >
          {facetOptions.languages.length > 1 && (
            <TextField
              select
              size="small"
              label={t('axisPage.filter.language')}
              value={langFilter}
              onChange={(e) => setLangFilter(e.target.value)}
              sx={{ minWidth: 130 }}
            >
              <MenuItem value="__all__">{t('axisPage.filter.allFeminine')}</MenuItem>
              {facetOptions.languages.map((l) => (
                <MenuItem key={l} value={l}>
                  {l.toUpperCase()}
                </MenuItem>
              ))}
            </TextField>
          )}
          {facetOptions.topics.length > 0 && (
            <TextField
              select
              size="small"
              label={axis === 'referral' ? t('axisPage.filter.useCase') : t('axisPage.filter.theme')}
              value={topicFilter}
              onChange={(e) => setTopicFilter(e.target.value)}
              sx={{ minWidth: 180 }}
            >
              <MenuItem value="__all__">{t('axisPage.filter.allMasculine')}</MenuItem>
              {facetOptions.topics.map((t) => (
                <MenuItem key={t} value={t}>
                  {t}
                </MenuItem>
              ))}
            </TextField>
          )}
          {facetOptions.zones.length > 1 && (
            <TextField
              select
              size="small"
              label={t('axisPage.filter.zone')}
              value={zoneFilter}
              onChange={(e) => setZoneFilter(e.target.value)}
              sx={{ minWidth: 130 }}
            >
              <MenuItem value="__all__">{t('axisPage.filter.allFeminine')}</MenuItem>
              {facetOptions.zones.map((z) => (
                <MenuItem key={z} value={z}>
                  {z}
                </MenuItem>
              ))}
            </TextField>
          )}
          <Box sx={{ flex: 1 }} />
          <Typography variant="caption" color="text.secondary">
            {t('axisPage.filter.promptCount', { visible: visibleRows.length, total: rows.length })}
          </Typography>
          {activeFilterCount > 0 && (
            <Button
              size="small"
              variant="text"
              onClick={() => {
                setLangFilter('__all__');
                setTopicFilter('__all__');
                setZoneFilter('__all__');
              }}
              sx={{ textTransform: 'none' }}
            >
              {t('axisPage.filter.reset')}
            </Button>
          )}
        </Box>
      )}
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: '38%', bgcolor: '#FAFBFC' }}>{t('axisPage.matrix.promptHeader')}</TableCell>
              {orderedAudits.map((a) => {
                const isLatest = a.auditId === latestAuditId;
                return (
                  <TableCell
                    key={a.auditId}
                    align="center"
                    sx={{
                      bgcolor: isLatest ? `${latestAuditMeta.color}14` : '#FAFBFC',
                      fontSize: '0.72rem',
                      color: isLatest ? latestAuditMeta.color : 'text.secondary',
                      textTransform: 'uppercase',
                      fontWeight: 700,
                      minWidth: 80,
                      position: 'relative',
                    }}
                  >
                    <Box sx={{ textAlign: 'center' }}>
                      <Box sx={{ fontSize: '0.78rem' }}>
                        {formatDate(a.completedAt, {
                          day: '2-digit',
                          month: 'short',
                        })}
                      </Box>
                      <Box sx={{ fontSize: '0.65rem', fontWeight: 400, mt: 0.25 }}>
                        {isLatest ? t('axisPage.matrix.latest') : ''}
                      </Box>
                    </Box>
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={orderedAudits.length + 1} sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>
                  {t('axisPage.matrix.noMatch')}
                </TableCell>
              </TableRow>
            )}
            {visibleRows.map((row) => {
              const isOpen = expanded === row.queryTextHash;
              const latestScore = matrix[0]?.queryScores?.find(
                (q) => q.queryTextHash === row.queryTextHash,
              );
              const queryEntry = latestScore;
              return (
                <>
                  <TableRow
                    key={row.queryTextHash}
                    hover
                    sx={{ cursor: 'pointer', bgcolor: isOpen ? `${latestAuditMeta.color}08` : undefined }}
                    onClick={() => handleExpand(row.queryTextHash)}
                  >
                    <TableCell sx={{ verticalAlign: 'top' }}>
                      <Typography
                        variant="body2"
                        fontWeight={500}
                        sx={{
                          textDecoration: row.archived ? 'line-through' : 'none',
                          color: row.archived ? 'text.secondary' : 'text.primary',
                        }}
                      >
                        {row.text}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
                        {row.archived && (
                          <Chip
                            label={t('axisPage.matrix.archived')}
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: '0.65rem',
                              fontWeight: 700,
                              bgcolor: '#F3F4F6',
                              color: '#6B7280',
                            }}
                          />
                        )}
                        {row.archived && row.canReactivate && (
                          <Button
                            size="small"
                            variant="text"
                            disabled={reactivating === row.queryTextHash}
                            onClick={(e) => {
                              e.stopPropagation();
                              setReactivating(row.queryTextHash);
                              const op = row.queryId
                                ? onReactivate(row.queryId)
                                : onReactivateByText(row.text);
                              op.finally(() => setReactivating(null));
                            }}
                            sx={{
                              minWidth: 0,
                              py: 0,
                              px: 0.75,
                              height: 18,
                              fontSize: '0.65rem',
                              fontWeight: 700,
                              textTransform: 'none',
                            }}
                          >
                            {reactivating === row.queryTextHash ? '…' : t('axisPage.matrix.reactivate')}
                          </Button>
                        )}
                        {!row.archived && row.queryId && (
                          <Button
                            size="small"
                            variant="text"
                            color="inherit"
                            disabled={reactivating === row.queryTextHash}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!row.queryId) return;
                              setReactivating(row.queryTextHash);
                              onArchive(row.queryId).finally(() => setReactivating(null));
                            }}
                            sx={{
                              minWidth: 0,
                              py: 0,
                              px: 0.75,
                              height: 18,
                              fontSize: '0.65rem',
                              color: '#9CA3AF',
                              textTransform: 'none',
                              '&:hover': { color: '#EF4444', bgcolor: 'rgba(239,68,68,0.06)' },
                            }}
                          >
                            {reactivating === row.queryTextHash ? '…' : t('axisPage.matrix.archive')}
                          </Button>
                        )}
                        {/* Level chip — prefer the active BrandQuery row's level
                            (always present, even before any audit), fall back
                            to the matrix entry for archived prompts. */}
                        {(row.level || queryEntry?.level) && (
                          <Chip
                            label={row.level || queryEntry?.level}
                            size="small"
                            sx={{ height: 18, fontSize: '0.65rem' }}
                          />
                        )}
                        {row.language && (
                          <Chip
                            label={row.language.toUpperCase()}
                            size="small"
                            variant="outlined"
                            sx={{ height: 18, fontSize: '0.65rem' }}
                          />
                        )}
                        {row.topic && (
                          <Chip
                            label={row.topic}
                            size="small"
                            variant="outlined"
                            sx={{
                              height: 18,
                              fontSize: '0.65rem',
                              borderColor: `${latestAuditMeta.color}55`,
                              color: latestAuditMeta.color,
                            }}
                          />
                        )}
                        {row.zone && (
                          <Typography variant="caption" color="text.secondary">
                            · {row.zone}
                          </Typography>
                        )}
                        {/* Best position across engines (referral only). Top
                            spot gets a vivid chip; 2-3 a softer one; 4+ a
                            muted caption — the color tracks the score-band
                            heuristic. */}
                        {axis === 'referral' && queryEntry?.bestPosition && (
                          <Chip
                            label={`#${queryEntry.bestPosition}`}
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: '0.65rem',
                              fontWeight: 700,
                              bgcolor:
                                queryEntry.bestPosition === 1
                                  ? '#DCFCE7'
                                  : queryEntry.bestPosition <= 3
                                    ? '#FEF3C7'
                                    : '#F3F4F6',
                              color:
                                queryEntry.bestPosition === 1
                                  ? '#15803D'
                                  : queryEntry.bestPosition <= 3
                                    ? '#92400E'
                                    : '#6B7280',
                            }}
                          />
                        )}
                        {queryEntry?.alignment && (
                          <Typography
                            variant="caption"
                            sx={{ color: ALIGNMENT_COLORS[queryEntry.alignment] || '#888' }}
                          >
                            ● {ALIGNMENT_LABEL_KEYS[queryEntry.alignment] ? t(ALIGNMENT_LABEL_KEYS[queryEntry.alignment]) : queryEntry.alignment}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    {orderedAudits.map((a) => {
                      const isLatest = a.auditId === latestAuditId;
                      // When a row is expanded, the cell that contains the
                      // *currently displayed* response prose (the source audit
                      // we actually fetched) gets the strong highlight — that's
                      // the latest audit for active prompts, but an older one
                      // for archived/orphan prompts.
                      const sourceAuditId = isOpen
                        ? resultsByHash[row.queryTextHash]?.auditId
                        : undefined;
                      const isSource = isOpen && sourceAuditId === a.auditId;
                      const cell = a.queryScores?.find((q) => q.queryTextHash === row.queryTextHash);
                      if (!cell) {
                        return (
                          <TableCell key={a.auditId} align="center" sx={{ color: '#D1D5DB' }}>
                            —
                          </TableCell>
                        );
                      }
                      const colors = scoreCellColor(cell.score);
                      let boxShadow = 'none';
                      if (isSource) {
                        // 4px ring + drop shadow for the source-audit cell.
                        boxShadow = `0 0 0 4px ${latestAuditMeta.color}, 0 4px 12px ${latestAuditMeta.color}55`;
                      } else if (isLatest) {
                        boxShadow = `0 0 0 2px ${latestAuditMeta.color}`;
                      }
                      const isSelected = selectedAuditsForComparison.get(row.queryTextHash)?.has(a.auditId) ?? false;
                      return (
                        <TableCell
                          key={a.auditId}
                          align="center"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (orderedAudits.length > 1) {
                              toggleAuditForComparison(row.queryTextHash, a.auditId);
                            }
                          }}
                          sx={{
                            cursor: orderedAudits.length > 1 ? 'pointer' : 'default',
                            transition: 'background-color 0.18s ease',
                            bgcolor: isSelected ? `${latestAuditMeta.color}22` : 'transparent',
                          }}
                        >
                          <Box
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              minWidth: 44,
                              height: 28,
                              px: 1,
                              borderRadius: 1,
                              bgcolor: colors.bg,
                              color: colors.fg,
                              fontWeight: 700,
                              fontSize: '0.85rem',
                              boxShadow: isSelected ? `0 0 0 2px ${latestAuditMeta.color}` : boxShadow,
                              transition: 'box-shadow 0.18s ease',
                            }}
                          >
                            {cell.score}
                          </Box>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                  {isOpen && (() => {
                    const fetched = resultsByHash[row.queryTextHash];
                    const isFromLatest = fetched?.auditId === latestAudit.auditId;
                    const fromDate = fetched
                      ? formatDate(fetched.completedAt, {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                        })
                      : null;

                    return (
                      <TableRow>
                        <TableCell colSpan={orderedAudits.length + 1} sx={{ p: 0, bgcolor: `${latestAuditMeta.color}06` }}>
                          <Box sx={{ p: 2 }}>
                            {/* Authority-only quick actions for an under-performing
                                prompt: seed the Studio with a content topic or open
                                the Brand Knowledge + GEO strategy suggestion diff. */}
                            {axis === 'authority' &&
                              !row.archived &&
                              queryEntry?.score != null &&
                              queryEntry.score < LOW_SCORE_THRESHOLD && (
                                <AuthorityPromptActions
                                  brandId={brandId}
                                  auditId={latestAudit.auditId}
                                  promptText={row.text}
                                  contentCreated={contentCreated.has(contentActionId(row.queryTextHash))}
                                  onContentCreated={() => markContentCreated(row.queryTextHash)}
                                />
                              )}

                            {/* Affichage des audits sélectionnés */}
                            {(() => {
                              const selectedSet = selectedAuditsForComparison.get(row.queryTextHash) || new Set();

                              if (selectedSet.size > 0) {
                                // Mode comparaison : afficher grille côte-à-côte (sans titre)
                                return (
                                  <Box sx={{ display: 'grid', gridTemplateColumns: selectedSet.size === 2 ? '1fr 1fr' : '1fr', gap: 2 }}>
                                    {Array.from(selectedSet).sort((aId1, aId2) => {
                                      // Trier par audit date: plus ancien à gauche, plus récent à droite
                                      const a1 = orderedAudits.find(a => a.auditId === aId1);
                                      const a2 = orderedAudits.find(a => a.auditId === aId2);
                                      return (a1?.completedAt || '').localeCompare(a2?.completedAt || '');
                                    }).map((selectedAuditId) => {
                                      const selectedAudit = matrix.find((a) => a.auditId === selectedAuditId);
                                      const selectedResults = fetchedAuditsRef.get(selectedAuditId) || [];
                                      const selectedFiltered = selectedResults.filter(
                                        (r) => (r.queryTextHash || '') === row.queryTextHash,
                                      );
                                      const selectedDate = selectedAudit?.completedAt
                                        ? formatDate(selectedAudit.completedAt, {
                                            day: '2-digit',
                                            month: 'short',
                                            year: 'numeric',
                                          })
                                        : null;
                                      return (
                                        <Box key={selectedAuditId}>
                                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontWeight: 600 }}>
                                            {t('axisPage.responses.fromDate', { date: selectedDate })}
                                          </Typography>
                                          <ExpandedQueryPanel
                                            rows={selectedFiltered}
                                            brand={brand}
                                            selectedEngine={selectedEngineByQuery.get(row.queryTextHash) ?? null}
                                            onEngineChange={(eng) => setSelectedEngineByQuery((prev) => {
                                              const next = new Map(prev);
                                              next.set(row.queryTextHash, eng);
                                              return next;
                                            })}
                                          />
                                        </Box>
                                      );
                                    })}
                                  </Box>
                                );
                              } else {
                                // Mode normal : afficher titre + l'audit principal
                                return (
                                  <>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2, fontWeight: 600 }}>
                                      {!fetched
                                        ? t('axisPage.responses.title')
                                        : isFromLatest
                                          ? t('axisPage.responses.latest')
                                          : t('axisPage.responses.fromDate', { date: fromDate })}
                                    </Typography>
                                    {loadingHash === row.queryTextHash ? (
                                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                                        <CircularProgress size={24} />
                                      </Box>
                                    ) : (
                                      <ExpandedQueryPanel
                                        rows={fetched?.rows || []}
                                        brand={brand}
                                        selectedEngine={selectedEngineByQuery.get(row.queryTextHash) ?? null}
                                        onEngineChange={(eng) => setSelectedEngineByQuery((prev) => {
                                          const next = new Map(prev);
                                          next.set(row.queryTextHash, eng);
                                          return next;
                                        })}
                                      />
                                    )}
                                  </>
                                );
                              }
                            })()}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })()}
                </>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  );
}

function ExpandedQueryPanel({
  rows,
  brand,
  selectedEngine,
  onEngineChange,
}: {
  rows: any[];
  brand: any;
  selectedEngine?: EngineKey | null;
  onEngineChange?: (engine: EngineKey) => void;
}) {
  const { t } = useTranslation();
  // Use external selectedEngine if provided (for sync across panels), otherwise determine default
  const currentEngine = selectedEngine ?? null;

  // Left-to-right tab order. AI Overview leads (it always searches, so it always
  // has citations); Mistral, the other secondary engine, stays last.
  const ENGINE_ORDER: EngineKey[] = [
    'ai_overview',
    'perplexity',
    'openai',
    'gemini',
    'claude',
    'mistral',
  ];
  const byEngine = new Map<EngineKey, any>();
  for (const r of rows) {
    byEngine.set(engineKey(r.engine), r);
  }
  const orderedEngines = ENGINE_ORDER.filter((k) => byEngine.has(k));
  // Default to the first engine that actually returned sources — OpenAI/ChatGPT
  // never returns citations by design, so on tiers without Perplexity (e.g.
  // basic = ChatGPT + Gemini) the tab would otherwise open on the one engine
  // that always looks empty. Falls back to the first available engine when none
  // has sources. User selection (currentEngine) always wins.
  const defaultEngine =
    orderedEngines.find((k) => (byEngine.get(k)?.sources?.length ?? 0) > 0) ??
    orderedEngines[0];
  const current = currentEngine ?? defaultEngine;

  if (rows.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t('axisPage.noResponseForPrompt')}
      </Typography>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        {orderedEngines.map((k) => {
          const r = byEngine.get(k)!;
          const m = ENGINE_META[k];
          const active = current === k;
          return (
            <Box
              key={k}
              onClick={() => onEngineChange?.(k)}
              sx={{
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1.5,
                py: 0.75,
                borderRadius: 1,
                border: `1.5px solid ${active ? m.color : 'rgba(11,11,15,0.10)'}`,
                bgcolor: active ? m.bg : 'transparent',
              }}
            >
              <EngineLogo engine={k} size={18} />
              <Typography variant="body2" fontWeight={600} sx={{ color: active ? m.color : 'text.primary' }}>
                {m.label}
              </Typography>
              <Chip
                label={`${r.score}`}
                size="small"
                sx={{ height: 18, fontSize: '0.7rem', fontWeight: 700, bgcolor: 'rgba(11,11,15,0.04)' }}
              />
            </Box>
          );
        })}
      </Box>
      {current && byEngine.get(current) && (
        <EngineResponsePanel
          row={byEngine.get(current)}
          engine={current}
          brandId={brand?.brandId}
          brandName={brand?.name}
          brandUrl={brand?.url}
          aliases={brand?.strategy?.aliases}
          showSources={true}
          queryText={byEngine.get(current).query}
        />
      )}
    </Box>
  );
}

// ── Recommendations are rendered via the shared RecommendationList component
//    (see components/recommendations/). The former per-page RecommendationsBlock
//    + RecommendationCard were replaced by the unified layout.

/**
 * "Non cité ?" hint next to a row's Cité/Non cité chip. `is_brand_cited`
 * (scoring.py) only matches the FULL brand name or an explicit alias — a
 * multi-word brand referred to by a shorter name (e.g. "Vitalis" for
 * "Vitalis Poitiers") reads as not-cited until an alias is configured. This
 * lets the user add one inline, without leaving the page or re-validating
 * Brand Knowledge (`aliases` is excluded from the strategy staleness hash).
 */
/**
 * The row is named but steered away from: referral scores it 0 instead of the
 * cited-only 50. Mirrors the `sentiment === 'negative'` branch of
 * `scorer.compute_axis_score(axis='referral', ...)` — keep the two in lock-step,
 * this is the only thing that explains a 0 sitting next to a "Cité" chip.
 */
function isDisrecommended(row: any): boolean {
  return (
    (row.axis || '').toLowerCase() === 'referral' &&
    !!row.cited &&
    !row.recommended &&
    row.sentiment === 'negative'
  );
}

function NotCitedHint({ brandId, aliases }: { brandId?: string; aliases: string[] }) {
  const { t } = useTranslation();
  const toast = useToast();
  const { refreshBrands } = useBrand();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);
  const maxItems = BRAND_STRATEGY_LIMITS.aliases.maxItems;
  const maxChars = BRAND_STRATEGY_LIMITS.aliases.maxCharsPerItem;
  const atLimit = aliases.length >= maxItems;

  const close = () => {
    setAnchorEl(null);
    setValue('');
  };

  const handleAdd = async () => {
    const alias = value.trim();
    if (!alias || !brandId || saving || atLimit) return;
    setSaving(true);
    try {
      const next = [...aliases, alias];
      await api.patch(`/brands/${brandId}/strategy`, { aliases: next });
      await refreshBrands();
      toast.show(t('axisPage.engine.notCitedHint.success'));
      close();
    } catch {
      toast.show(t('axisPage.engine.notCitedHint.error'), { severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <IconButton
        size="small"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        title={t('axisPage.engine.notCitedHint.tooltip')}
        sx={{ width: 20, height: 20, color: 'text.disabled' }}
      >
        <HelpOutlineIcon sx={{ fontSize: 16 }} />
      </IconButton>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{ paper: { sx: { maxWidth: 340, p: 2 } } }}
      >
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
          {t('axisPage.engine.notCitedHint.popoverTitle')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {t('axisPage.engine.notCitedHint.popoverBody')}
        </Typography>
        {atLimit ? (
          <Alert severity="warning" sx={{ mb: 0 }}>
            {t('axisPage.engine.notCitedHint.limitReached', { max: maxItems })}
          </Alert>
        ) : (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              size="small"
              fullWidth
              autoFocus
              value={value}
              onChange={(e) => setValue(e.target.value.slice(0, maxChars))}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAdd();
                }
              }}
              placeholder={t('axisPage.engine.notCitedHint.placeholder')}
              inputProps={{ maxLength: maxChars }}
              disabled={saving}
            />
            <Button
              variant="contained"
              size="small"
              onClick={handleAdd}
              disabled={saving || !value.trim()}
              startIcon={saving ? <CircularProgress size={14} color="inherit" /> : undefined}
            >
              {t('axisPage.engine.notCitedHint.add')}
            </Button>
          </Box>
        )}
      </Popover>
    </>
  );
}

function EngineResponsePanel({
  row,
  engine,
  brandId,
  brandName,
  brandUrl,
  aliases,
  showSources,
  showEnhance,
  onEnhance,
  enhancingKey,
  queryText,
}: {
  row: any;
  engine: EngineKey;
  brandId?: string;
  brandName?: string;
  brandUrl?: string;
  aliases?: string[];
  showSources?: boolean;
  showEnhance?: boolean;
  onEnhance?: (query: string, response: string) => void;
  enhancingKey?: string | null;
  queryText?: string;
}) {
  const { t } = useTranslation();
  const meta = ENGINE_META[engine];
  // Same fetch as HighlightedResponse — the promise is cached per brand, so this
  // costs a hook, not a second request.
  const { colors: marqueColors, domainHexes } = useMarqueColors(brandId);
  const sourceColors = useMemo(
    () => sourceColorsFrom(row.targetSourceHighlights, marqueColors),
    [row.targetSourceHighlights, marqueColors],
  );
  const enhanceQuery = queryText || row.query;
  const enhanceLoading = !!enhanceQuery && enhancingKey === enhanceQuery;
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
        <EngineLogo engine={engine} size={20} />
        <Typography variant="subtitle2" fontWeight={700} sx={{ color: meta.color }}>
          {meta.label}
        </Typography>
        <Chip
          label={row.cited ? t('axisPage.engine.cited') : t('axisPage.engine.notCited')}
          size="small"
          color={row.cited ? 'success' : 'error'}
          variant="outlined"
          sx={{ height: 20 }}
        />
        {!row.cited && <NotCitedHint brandId={brandId} aliases={aliases || []} />}
        {/*
          `judgeFailed` ⇒ `recommended` / `sentiment` / `narrativeAlignment` are
          neutral DEFAULTS, not judgements (see `judgeFailed` in audit.ts). Showing
          them would render "Non recommandé · neutral" on an answer that may well
          recommend the brand — so show one honest chip instead. `cited` and the
          target chips are deterministic and stay.
        */}
        {row.judgeFailed ? (
          <Chip
            label={t('axisPage.engine.judgeFailed')}
            title={t('axisPage.engine.judgeFailedTooltip')}
            size="small"
            variant="outlined"
            sx={{ height: 20, borderStyle: 'dashed', color: 'text.disabled', fontStyle: 'italic' }}
          />
        ) : (
          <>
            <Chip
              label={
                row.recommended
                  ? row.recommendedPosition && row.recommendedTotal
                    ? t('axisPage.engine.recommendedPositionTotal', { position: row.recommendedPosition, total: row.recommendedTotal })
                    : row.recommendedPosition
                      ? t('axisPage.engine.recommendedPosition', { position: row.recommendedPosition })
                      : t('axisPage.engine.recommended')
                  : t('axisPage.engine.notRecommended')
              }
              // Cited + negative + not recommended is the engine steering the
              // buyer AWAY from the brand ("À éviter pour ton exigence de délai"),
              // which scores 0 rather than the cited-only 50. Say why, or a 0 next
              // to "Cité" reads as a bug.
              title={isDisrecommended(row) ? t('axisPage.engine.disrecommendedTooltip') : undefined}
              size="small"
              color={row.recommended ? 'success' : isDisrecommended(row) ? 'error' : 'default'}
              variant="outlined"
              sx={{ height: 20 }}
            />
            <Chip
              label={row.sentiment}
              size="small"
              sx={{
                height: 20,
                bgcolor:
                  row.sentiment === 'positive'
                    ? '#00DDB320'
                    : row.sentiment === 'negative'
                      ? '#FF444420'
                      : '#8B9DC320',
                color:
                  row.sentiment === 'positive'
                    ? '#00DDB3'
                    : row.sentiment === 'negative'
                      ? '#FF4444'
                      : '#8B9DC3',
                fontWeight: 600,
              }}
            />
            {row.narrativeAlignment && (
              <Chip
                label={t('axisPage.engine.alignmentLabel', { value: ALIGNMENT_LABEL_KEYS[row.narrativeAlignment] ? t(ALIGNMENT_LABEL_KEYS[row.narrativeAlignment]) : row.narrativeAlignment })}
                size="small"
                sx={{
                  height: 20,
                  bgcolor: `${ALIGNMENT_COLORS[row.narrativeAlignment] || '#888'}20`,
                  color: ALIGNMENT_COLORS[row.narrativeAlignment] || '#888',
                  fontWeight: 600,
                }}
              />
            )}
          </>
        )}
        {(row.targetCategories || []).map(({ category, count }: { category: string; count: number }) => {
          const cc = CATEGORY_CHIP_COLOR[category];
          if (!cc) return null;
          return (
            <Chip
              key={category}
              label={count > 1
                ? `${t(`cibles.tab.${category}`)} ×${count}`
                : t(`cibles.tab.${category}`)}
              size="small"
              sx={{ height: 20, bgcolor: cc.bg, color: cc.fg, fontWeight: 600 }}
            />
          );
        })}
      </Box>

      {row.alignmentReason &&
        (row.narrativeAlignment === 'weak' ||
          row.narrativeAlignment === 'misaligned' ||
          row.narrativeAlignment === 'partial') && (
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              mb: 1,
              p: 1,
              borderRadius: 1,
              bgcolor: `${ALIGNMENT_COLORS[row.narrativeAlignment] || '#888'}12`,
            }}
          >
            <InfoOutlinedIcon
              sx={{ fontSize: 16, mt: '2px', color: ALIGNMENT_COLORS[row.narrativeAlignment] || '#888' }}
            />
            <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.5 }}>
              {row.alignmentReason}
            </Typography>
          </Box>
        )}

      <Box
        sx={{
          bgcolor: 'rgba(11,11,15,0.03)',
          borderLeft: `3px solid ${meta.color}`,
          borderRadius: 1,
          p: 2,
        }}
      >
        <HighlightedResponse
          text={row.response || ''}
          brandId={brandId}
          sources={row.sources || []}
          sourceLinks={row.sourceLinks || []}
          targetHighlights={row.targetHighlights}
          targetSourceHighlights={row.targetSourceHighlights}
        />
      </Box>

      {showSources && row.sources && row.sources.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            {t('axisPage.engine.sources', { count: row.sources.length })}
            {row.brandIsSource && (
              <Chip label={t('axisPage.engine.yourSiteIsSource')} size="small" color="success" sx={{ ml: 1 }} />
            )}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {row.sources.map((url: string, si: number) => {
              const isBrand = isBrandUrl(url, brandName, brandUrl);
              const domain = extractDomain(url);
              // Chip colour priority (D45):
              //   1. Registered Cibles target matched this source → target's marque colour.
              //      Also covers brand-domain URLs that are registered targets (e.g.
              //      `salon.dessange.com` as a marque domain target → DESSANGE blue, not green).
              //   2. Brand domain but no registered target → look up the marque whose
              //      `domain` field owns this host and use its configured colour.  This
              //      covers every unregistered page on the brand's domain(s) — a blog post,
              //      a /catalogue page — so the chip is consistent with the target chips.
              //      Falls back to the generic green only when no marque has a domain that
              //      matches (brand has no marques configured, or the domain differs).
              //   3. Neither → default purple outline (third-party source, no target).
              const targetHex = sourceColors.get(si);
              // Brand-domain chips with no registered target: look up the marque whose
              // domain field matches, so the chip uses the configured brand colour.
              // Subdomain and sibling-TLD matches are handled by the isBrandOwnedHost loop.
              const brandDomainHex = (!targetHex && isBrand && brandUrl)
                ? (() => {
                    const bh = bareHost(brandUrl);
                    if (domainHexes[bh]) return domainHexes[bh];
                    for (const [d, hex] of Object.entries(domainHexes)) {
                      if (isBrandOwnedHost(bh, d) || isBrandOwnedHost(d, bh)) return hex;
                    }
                    return undefined;
                  })()
                : undefined;
              const activeHex = targetHex ?? brandDomainHex;
              const href = sourceHref(row.sourceLinks?.[si] || url);
              return (
                <Chip
                  key={si}
                  label={domain.slice(0, 50)}
                  size="small"
                  variant={activeHex || isBrand ? 'filled' : 'outlined'}
                  // Never surface the opaque wrapper URL as a tooltip.
                  title={href && !isGroundingWrapper(href) ? href : domain}
                  clickable={!!href}
                  {...(href
                    ? { component: 'a', href, target: '_blank', rel: 'noopener noreferrer' }
                    : {})}
                  sx={
                    activeHex
                      ? { bgcolor: `${activeHex}26`, color: activeHex, fontWeight: 700 }
                      : isBrand
                        ? { bgcolor: 'rgba(16, 185, 129, 0.12)', color: '#10B981', fontWeight: 700 }
                        : { bgcolor: 'rgba(124, 58, 237, 0.10)', borderColor: 'rgba(124, 58, 237, 0.32)', color: '#7C3AED' }
                  }
                />
              );
            })}
          </Box>
        </Box>
      )}

      {showEnhance && onEnhance && (
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={
              enhanceLoading ? (
                <CircularProgress size={14} sx={{ color: 'primary.main' }} />
              ) : (
                <TrendingUpIcon sx={{ color: 'primary.main' }} />
              )
            }
            disabled={enhanceLoading || !enhanceQuery}
            onClick={() => onEnhance(enhanceQuery, row.response || '')}
            sx={{
              borderColor: 'rgba(124, 58, 237, 0.5)',
              color: 'primary.main',
              '&:hover': { borderColor: 'primary.main', bgcolor: 'rgba(124, 58, 237, 0.08)' },
            }}
          >
            {enhanceLoading ? t('axisPage.engine.analyzing') : t('axisPage.engine.appearMore')}
          </Button>
        </Box>
      )}
    </Box>
  );
}
